import { useMutation, useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/apiClient";
import type {
  BusinessAdvisorLookupItem,
  CreateSoftHoldRequest,
  ResolveSoftHoldRequest,
  SoftHoldCategoryOption,
  SoftHoldHistoryResponse,
  SoftHoldMeetingTypeOption,
  SoftHoldResponse,
  SoftHoldSubCategoryOption,
  SummitMeetingLookupItem,
  UpdatePreferredDateSelectionStatusRequest
} from "@/types/softHolds";

export const softHoldKeys = {
  byMonth: (month: number, year: number) => ["soft-holds", "month", month, year] as const,
  byId: (softHoldId: number) => ["soft-holds", "id", softHoldId] as const,
  historyById: (softHoldId: number) => ["soft-holds", "history", softHoldId] as const
};

export function useSoftHoldsByMonth(month: number, year: number) {
  return useQuery({
    queryKey: softHoldKeys.byMonth(month, year),
    queryFn: () => apiFetch<SoftHoldResponse[]>(`SoftHolds/GetSoftHoldsByMonth/${month}/${year}`),
    staleTime: 30_000,
    enabled: month > 0 && year > 0
  });
}

export function useSoftHoldById(softHoldId: number, enabled = true) {
  return useQuery({
    queryKey: softHoldKeys.byId(softHoldId),
    queryFn: () => apiFetch<SoftHoldResponse>(`SoftHolds/GetSoftHoldsById/${softHoldId}`),
    enabled: enabled && softHoldId > 0,
    staleTime: 30_000
  });
}

export function useSoftHoldHistoryById(softHoldId: number, enabled = true) {
  return useQuery({
    queryKey: softHoldKeys.historyById(softHoldId),
    queryFn: () =>
      apiFetch<SoftHoldHistoryResponse[]>(`SoftHolds/GetSoftholdsHistoryById/${softHoldId}`),
    enabled: enabled && softHoldId > 0,
    staleTime: 15_000
  });
}

export function useUpdatePreferredDateSelectionStatus() {
  return useMutation({
    mutationFn: (body: UpdatePreferredDateSelectionStatusRequest) =>
      apiFetch<SoftHoldResponse>("SoftHolds/UpdatePreferDateSelectionStatus", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useResolveSoftHold() {
  return useMutation({
    mutationFn: (body: ResolveSoftHoldRequest) =>
      apiFetch<boolean>("SoftHolds/ResolveSoftHolds", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useCreateSoftHold() {
  return useMutation({
    mutationFn: (body: CreateSoftHoldRequest) =>
      apiFetch<number>("SoftHolds/CreateSoftHolds", {
        method: "POST",
        body: JSON.stringify(body)
      })
  });
}

export function useSoftHoldMeetingTypes() {
  return useQuery({
    queryKey: ["soft-holds", "meeting-types"],
    queryFn: () => apiFetch<SoftHoldMeetingTypeOption[]>("SoftHolds/GetMeetingTypesList"),
    staleTime: 60_000
  });
}

export function useSoftHoldCategories(meetingTypeId: number | null) {
  return useQuery({
    queryKey: ["soft-holds", "categories", meetingTypeId],
    queryFn: () =>
      apiFetch<SoftHoldCategoryOption[]>(`SoftHolds/GetCategoriesByMeetingType/${meetingTypeId}`),
    enabled: meetingTypeId !== null && meetingTypeId > 0,
    staleTime: 60_000
  });
}

export function useSoftHoldSubCategories(categoryId: number | null) {
  return useQuery({
    queryKey: ["soft-holds", "sub-categories", categoryId],
    queryFn: () =>
      apiFetch<SoftHoldSubCategoryOption[]>(`SoftHolds/GetSubCategoriesByCategory/${categoryId}`),
    enabled: categoryId !== null && categoryId > 0,
    staleTime: 60_000
  });
}

export function useSummitMeetingLookup(summitMeetingId: string, enabled = true) {
  const normalizedValue = summitMeetingId.trim();

  return useQuery({
    queryKey: ["summit", "meeting-details", normalizedValue],
    queryFn: () =>
      apiFetch<SummitMeetingLookupItem[]>(
        `Summit/${encodeURIComponent(normalizedValue)}/meetingDetails`
      ),
    enabled: enabled && normalizedValue.length > 0,
    staleTime: 30_000
  });
}

export function useBusinessAdvisorLookup(searchTerm: string, enabled = true) {
  const normalizedValue = searchTerm.trim();

  return useQuery({
    queryKey: ["summit", "business-advisors", normalizedValue],
    queryFn: () =>
      apiFetch<BusinessAdvisorLookupItem[]>(
        `Summit/${encodeURIComponent(normalizedValue)}/businessAdvisors`
      ),
    enabled: enabled && normalizedValue.length > 0,
    staleTime: 30_000
  });
}
